import { useRef, useState, useCallback, useEffect } from 'react';
import { DemoDrinkLogSection } from './DemoDrinkLogSection';
import { DemoRewardsSection } from './DemoRewardsSection';
import { DemoLeaderboardSection } from './DemoLeaderboardSection';
import { DemoProfileSection } from './DemoProfileSection';
import { DemoIndicator } from './DemoIndicator';
import { DemoConversionCta } from './DemoConversionCta';
import { OnePageNav } from '@/components/OnePageNav';
import { DemoHeader } from './DemoHeader';

export type SectionId = 'drink-log' | 'rewards' | 'leaderboard' | 'profile';

export default function DemoOnePager() {
  const [activeSection, setActiveSection] = useState<SectionId>('drink-log');
  const [isModalOpen, setIsModalOpen] = useState(false);

  const drinkLogRef = useRef<HTMLElement>(null);
  const rewardsRef = useRef<HTMLElement>(null);
  const leaderboardRef = useRef<HTMLElement>(null);
  const profileRef = useRef<HTMLElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const sectionRefs = {
    'drink-log': drinkLogRef,
    'rewards': rewardsRef,
    'leaderboard': leaderboardRef,
    'profile': profileRef,
  };

  // Scroll to section
  const scrollToSection = useCallback((sectionId: SectionId) => {
    const ref = sectionRefs[sectionId];
    if (ref?.current) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  // Track active section based on scroll position
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const scrollTop = container.scrollTop;
      const sections: SectionId[] = ['drink-log', 'rewards', 'leaderboard', 'profile'];
      
      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i];
        const ref = sectionRefs[section];
        if (ref.current) {
          const offsetTop = ref.current.offsetTop - container.offsetTop;
          if (scrollTop >= offsetTop - 100) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle modal open/close for scroll locking
  const handleModalOpen = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  return (
    <div className="min-h-screen-safe bg-background flex flex-col">
      {/* Demo Indicator */}
      <DemoIndicator />
      
      {/* Header */}
      <DemoHeader />

      {/* Scrollable Content */}
      <div 
        ref={containerRef}
        className={`flex-1 overflow-y-auto pb-[140px] ${isModalOpen ? 'overflow-hidden' : ''}`}
        style={{ scrollBehavior: 'smooth' }}
      >
        <DemoDrinkLogSection 
          ref={drinkLogRef}
          onModalOpen={handleModalOpen}
          onModalClose={handleModalClose}
        />
        
        <DemoRewardsSection 
          ref={rewardsRef}
          onModalOpen={handleModalOpen}
          onModalClose={handleModalClose}
        />
        
        <DemoLeaderboardSection 
          ref={leaderboardRef}
        />

        <DemoProfileSection
          ref={profileRef}
        />
      </div>

      {/* Bottom Navigation - Fixed with safe area */}
      <div className="fixed left-0 right-0 z-40" style={{ bottom: 'calc(52px + env(safe-area-inset-bottom, 0px))' }}>
        <OnePageNav 
          activeSection={activeSection}
          onNavigate={scrollToSection}
          onNavClick={() => {}}
        />
      </div>

      {/* Sticky Conversion CTA - Fixed at very bottom with safe area */}
      <DemoConversionCta variant="sticky" />
    </div>
  );
}
